import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  ChangeEvent,
  useImperativeHandle,
} from "react";
import { useParams } from "react-router-dom";
import { IoAddCircle } from "react-icons/io5";
import { CiCircleRemove } from "react-icons/ci";
import { RiMailSettingsLine } from "react-icons/ri";
import { toast } from "react-toastify";
import { GetGroupUserInfo } from "../../../../utils/GroupNotificationUtils";
import { LanguageText } from "../../../../dictionary/GroupNotificationText";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { MdOutlineError } from "react-icons/md";
import { FaEnvelopeOpenText } from "react-icons/fa";
import { LuArrowUpWideNarrow, LuArrowDownWideNarrow } from "react-icons/lu";
import ToggleSwitch from "../../../../utils/ToggleSwitch";
import { remove } from "lodash";

interface UserListProps {
  isNew: boolean;
  members: any[];
  users: string[];
  removeUser: (userID: string) => void;
  onAddUser: (userID: string) => void;
  error: boolean;
}

const Recipients = ({
  isNew,
  members,
  users,
  removeUser,
  onAddUser,
  error,
}: UserListProps): JSX.Element => {
  const Language: LanguageText = useGetLanguage({
    fileName: "GroupNotificationText",
  });

  const { groupid } = useParams<{ groupid: string }>();
  const membersListRef = useRef<HTMLUListElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortedMembers, setSortedMembers] = useState<any[]>([]);

  useEffect(() => {
    if (members.length) {
      const sorted = [...members].sort((a, b) =>
        sortOrder === "asc"
          ? a.username?.localeCompare(b.username || "")
          : b.username?.localeCompare(a.username || "")
      );
      setSortedMembers(sorted);
    }
  }, [members, sortOrder]);

  useEffect(() => {
    const listElement = membersListRef.current;
    const scrollIndicatorElement = scrollRef.current;

    const handleScroll = () => {
      if (listElement && scrollIndicatorElement) {
        const scrollPercentage =
          (listElement.scrollTop /
            (listElement.scrollHeight - listElement.clientHeight)) *
          100;
        scrollIndicatorElement.style.width = `${scrollPercentage}%`;
      }
    };

    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const List = ({
    groupid,
    userid,
  }: {
    groupid: string;
    userid: string;
  }): JSX.Element => {
    const { data } = GetGroupUserInfo({ groupid, userid });
    const isRecipient = users?.includes(userid);

    return (
      <li
        key={userid}
        className={`flex items-center justify-between py-2 px-3 sm:px-4 rounded-md border transition-all duration-200 ${
          isRecipient
            ? "bg-blue-50 border-blue-200 hover:bg-blue-100"
            : "bg-gray-50 border-gray-200 hover:bg-gray-100 opacity-75"
        }`}
      >
        <div className="flex-1 min-w-0">
          <h3
            className={`text-sm sm:text-base font-medium truncate ${
              isRecipient ? "text-blue-800" : "text-gray-700"
            }`}
          >
            {data?.username}
          </h3>
          <p
            className={`text-xs sm:text-sm truncate ${
              isRecipient ? "text-blue-600" : "text-gray-500"
            }`}
          >
            {data?.email}
          </p>
        </div>
        <ToggleSwitch
          isActive={isRecipient}
          onToggle={() => (isRecipient ? removeUser(userid) : onAddUser(userid))}
        />
      </li>
    );
  };

  const handleSortToggle = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  return (
    <div className="w-full mt-1">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-gray-700 font-medium text-sm sm:text-base">
          {`${Language?.recipients} ${users.length}/${members.length} ${Language?.added}`}
        </label>
      </div>
      {!isNew && !users.length && (
        <div className="flex items-center mt-3">
          <MdOutlineError className="text-red-500 text-lg mr-2" />
          <span className="text-xs text-red-500">{Language?.recipientsErrorMsg}</span>
        </div>
      )}
      <div className="bg-white border border-gray-200 shadow-md rounded-md overflow-hidden w-full sm:w-2/3 lg:w-1/2 transition-all duration-300">
        {members.length ? (
          <>
            <button
              type="button"
              onClick={handleSortToggle}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors duration-150 text-sm"
            >
              {sortOrder === "asc" ? (
                <>
                  <LuArrowUpWideNarrow size={18} />
                  Asc
                </>
              ) : (
                <>
                  <LuArrowDownWideNarrow size={18} />
                  Desc
                </>
              )}
            </button>
            <ul
              ref={membersListRef}
              className="divide-y divide-gray-200 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-gray-100"
            >
              {sortedMembers.map((member: any) => (
                <List key={member.id} groupid={groupid || ""} userid={member.id} />
              ))}
            </ul>

            {/* Scroll progress bar */}
            <div ref={scrollRef} className="relative h-1 bg-gray-200 w-full">
              <div className="absolute top-0 left-0 h-1 bg-blue-500 transition-all duration-300"></div>
            </div>
          </>
        ) : (
          <div className="flex flex-col justify-center items-center p-6 text-center text-gray-500">
            <FaEnvelopeOpenText className="text-6xl mb-4 text-gray-400" />
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center mt-3">
          <MdOutlineError className="text-red-500 text-lg mr-2" />
          <span className="text-xs text-red-500">{Language?.recipientsErrorMsg}</span>
        </div>
      )}
    </div>
  );
};

export default Recipients;
